// Floor-plan sidebar with a "you-are-here" arrow that follows the camera's
// current yaw. Replaces the simple grid-of-rooms minimap with a shape-aware
// 2D plan rendered inline as SVG.
//
// Coordinate convention: apartment-frame metres, +X right, +Y "up" the page
// (inverted to SVG's +Y-down at render time). Each room declares a `shape`
// polygon and a `cameraXY` anchor in this frame.

import type { ApartmentFeature, Room } from '../app/types';

export type FloorplanSelectHandler = (room: Room) => void;

export class Floorplan {
  private readonly root: HTMLElement;
  private rooms: readonly Room[] = [];
  private features: readonly ApartmentFeature[] = [];
  private currentRoomId: string | null = null;
  private cameraYawDeg = 0;
  private handler: FloorplanSelectHandler = () => {};
  private isOpen = true;
  private padding = 4; // SVG viewBox padding (apartment-frame metres) — tighter so the plan fills the panel
  /** Direct reference to the you-are-here arrow SVG group; cached after each render so per-frame yaw updates skip a querySelector. */
  private arrowEl: SVGElement | null = null;

  constructor() {
    const el = document.getElementById('minimap');
    if (!el) throw new Error('[floorplan] #minimap missing');
    this.root = el;
    // The floor plan replaces the previous grid-of-buttons minimap markup.
    this.root.classList.add('floorplan');
    // The top-bar #floorplan-toggle button is the single canonical control
    // (wired in App.installFloorplanToggle). The legacy bottom-bar "Mapa"
    // pill was removed — its element no longer exists.
  }

  onSelect(handler: FloorplanSelectHandler): void {
    this.handler = handler;
  }

  setRooms(rooms: readonly Room[], currentRoomId: string, features: readonly ApartmentFeature[] = []): void {
    this.rooms = rooms;
    this.features = features;
    this.currentRoomId = currentRoomId;
    this.render();
    // Re-apply the CURRENT state rather than forcing show(). setRooms() runs
    // again on every locale switch (App.rerenderLocalizedData), and forcing it
    // open there re-opened a panel the visitor had deliberately closed.
    this.applyVisibility();
  }

  setCurrentRoom(roomId: string): void {
    this.currentRoomId = roomId;
    this.render();
  }

  /**
   * Push the live camera yaw (degrees) so the you-are-here arrow rotates as
   * the user drag-looks. App.ts wires this to the per-frame controls reading.
   *
   * Direction note: the arrow lives inside an SVG group that has `scale(1 -1)`
   * applied (apartment +Y → page up). A positive SVG rotation reads as
   * counter-clockwise in that flipped frame, which would be the opposite of
   * what a compass user expects. We negate the angle so drag-right → arrow
   * tip rotates clockwise on screen.
   */
  setCameraYaw(yawDeg: number): void {
    if (Math.abs(yawDeg - this.cameraYawDeg) < 0.1) return; // skip imperceptible deltas
    this.cameraYawDeg = yawDeg;
    if (!this.arrowEl) return;
    const cx = parseFloat(this.arrowEl.getAttribute('data-cx') ?? '0');
    const cy = parseFloat(this.arrowEl.getAttribute('data-cy') ?? '0');
    const yawOffset = parseFloat(this.arrowEl.getAttribute('data-yaw-offset') ?? '0');
    this.arrowEl.setAttribute('transform', `rotate(${-(yawDeg + yawOffset)} ${cx} ${cy})`);
  }

  show(): void {
    this.isOpen = true;
    this.applyVisibility();
  }

  hide(): void {
    this.isOpen = false;
    this.applyVisibility();
  }

  private applyVisibility(): void {
    this.root.classList.toggle('hidden', !this.isOpen);
  }

  toggle(): void {
    if (this.isOpen) this.hide();
    else this.show();
  }

  // ── Internals ─────────────────────────────────────────────────

  private render(): void {
    this.root.innerHTML = '';
    const planRooms = this.rooms.filter((r) => r.floorplan);
    if (planRooms.length === 0) {
      // Fallback to a label-only list if no floorplan data has been added yet.
      this.renderLabelGrid();
      return;
    }
    const bounds = this.computeBounds(planRooms, this.features);
    const w = bounds.maxX - bounds.minX;
    const h = bounds.maxY - bounds.minY;
    const viewBox = `${bounds.minX} ${bounds.minY} ${w} ${h}`;

    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('xmlns', svgNS);
    svg.classList.add('floorplan__svg');
    // Flip the Y axis so apartment +Y renders as "up" the SVG.
    const inner = document.createElementNS(svgNS, 'g');
    inner.setAttribute('transform', `translate(0 ${bounds.minY + bounds.maxY}) scale(1 -1)`);
    svg.appendChild(inner);

    for (const room of planRooms) {
      const fp = room.floorplan!;
      const points = fp.shape.map(([x, y]) => `${x},${y}`).join(' ');
      const poly = document.createElementNS(svgNS, 'polygon');
      poly.setAttribute('points', points);
      poly.setAttribute('class',
        `floorplan__room ${room.id === this.currentRoomId ? 'is-current' : ''}`,
      );
      poly.setAttribute('data-room-id', room.id);
      poly.style.cursor = 'pointer';
      poly.addEventListener('click', () => this.handler(room));
      inner.appendChild(poly);

      // Room name label, projected to centroid (rough average — fine for rectangles).
      const cx = fp.shape.reduce((s, p) => s + p[0], 0) / fp.shape.length;
      const cy = fp.shape.reduce((s, p) => s + p[1], 0) / fp.shape.length;
      const text = document.createElementNS(svgNS, 'text');
      text.setAttribute('x', String(cx));
      text.setAttribute('y', String(cy));
      // Counter-flip the text so it reads upright despite the y-axis flip.
      text.setAttribute('transform', `scale(1 -1) translate(0 ${-cy * 2})`);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'central');
      text.setAttribute('class', 'floorplan__label');
      text.textContent = room.name;
      inner.appendChild(text);
    }

    // Apartment-level features (staircase, openings…). Drawn between rooms
    // and the you-are-here arrow so the arrow always sits on top.
    for (const feature of this.features) {
      if (feature.type === 'staircase') {
        this.renderStaircase(inner, feature, svgNS);
      }
    }

    // You-are-here arrow at the current room's cameraXY. Sized in
    // apartment-frame metres so it scales with the SVG viewBox.
    this.arrowEl = null;
    const current = planRooms.find((r) => r.id === this.currentRoomId);
    if (current?.floorplan) {
      const [cx, cy] = current.floorplan.cameraXY;
      const yawOffset = current.floorplan.yawOffsetDeg ?? 0;
      const arrowGroup = document.createElementNS(svgNS, 'g');
      arrowGroup.setAttribute('data-floorplan-arrow', '1');
      arrowGroup.setAttribute('data-cx', String(cx));
      arrowGroup.setAttribute('data-cy', String(cy));
      arrowGroup.setAttribute('data-yaw-offset', String(yawOffset));
      // Negate so drag-right rotates the tip clockwise on screen — see
      // setCameraYaw() comment for the y-flip explanation.
      arrowGroup.setAttribute('transform', `rotate(${-(this.cameraYawDeg + yawOffset)} ${cx} ${cy})`);
      // Counter-flip the arrow so the geometry below renders right-side-up
      // under the parent y-axis flip.
      const arrowInner = document.createElementNS(svgNS, 'g');
      arrowInner.setAttribute('transform', `translate(${cx} ${cy}) scale(1 -1) translate(${-cx} ${-cy})`);
      // Centre dot — bigger so it reads as a clear "you are here" anchor.
      const dot = document.createElementNS(svgNS, 'circle');
      dot.setAttribute('cx', String(cx));
      dot.setAttribute('cy', String(cy));
      dot.setAttribute('r', '0.45');
      dot.setAttribute('class', 'floorplan__dot');
      arrowInner.appendChild(dot);
      // Arrow head (triangle pointing along +Y up the page after the
      // counter-flip). Doubled in size from the v1 0.7 m tall version so it
      // reads at a glance even on a phone-sized panel.
      const head = document.createElementNS(svgNS, 'polygon');
      const tipY = cy + 1.5;
      head.setAttribute('points',
        `${cx - 0.7},${cy + 0.1} ${cx + 0.7},${cy + 0.1} ${cx},${tipY}`,
      );
      head.setAttribute('class', 'floorplan__arrow');
      arrowInner.appendChild(head);
      arrowGroup.appendChild(arrowInner);
      inner.appendChild(arrowGroup);
      this.arrowEl = arrowGroup;
    }

    this.root.appendChild(svg);
  }

  private renderLabelGrid(): void {
    // Minimal fallback when no floorplan data is supplied — preserves legacy
    // behaviour so the UI still works during incremental rollout.
    for (const room of this.rooms) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'minimap__node';
      btn.textContent = room.name;
      btn.dataset.roomId = room.id;
      if (room.id === this.currentRoomId) btn.classList.add('is-current');
      btn.addEventListener('click', () => this.handler(room));
      this.root.appendChild(btn);
    }
  }

  private computeBounds(
    rooms: readonly Room[],
    features: readonly ApartmentFeature[] = [],
  ): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const room of rooms) {
      const fp = room.floorplan;
      if (!fp) continue;
      for (const [x, y] of fp.shape) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    for (const feature of features) {
      for (const [x, y] of feature.shape) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    return {
      minX: minX - this.padding,
      minY: minY - this.padding,
      maxX: maxX + this.padding,
      maxY: maxY + this.padding,
    };
  }

  /**
   * Draw a staircase polygon: pale background fill + a fan of tread lines
   * running perpendicular to `treadAngleDeg`. The hatch is the universal
   * floor-plan symbol for "stairs go here" — buyers read it instantly.
   */
  private renderStaircase(
    inner: SVGElement,
    feature: ApartmentFeature,
    svgNS: string,
  ): void {
    if (feature.shape.length < 3) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of feature.shape) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }

    const points = feature.shape.map(([x, y]) => `${x},${y}`).join(' ');
    const fill = document.createElementNS(svgNS, 'polygon');
    fill.setAttribute('points', points);
    fill.setAttribute('class', 'floorplan__stair-fill');
    inner.appendChild(fill);

    // Tread lines: parallel stripes spanning the bounding box, clipped to
    // the polygon. Treads run *along* treadAngleDeg, so the stripe
    // direction is perpendicular (angle + 90°). Sample N stripes evenly
    // across the polygon's narrow axis — for a 2×3.5m staircase that's
    // ~10–12 treads, matching the printed plan.
    const treadAngleDeg = feature.treadAngleDeg ?? 0;
    const treadRad = (treadAngleDeg * Math.PI) / 180;
    // Direction the treads RUN along (i.e. the long axis of a single tread).
    const ux = Math.cos(treadRad);
    const uy = Math.sin(treadRad);
    // Direction the stripes ADVANCE along (perpendicular: short axis).
    const sx = -uy;
    const sy = ux;

    const w = maxX - minX;
    const h = maxY - minY;
    // Project the bbox onto the stripe axis to find the stripe range.
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    // Half-diagonal — guarantees stripes always extend past the polygon.
    const halfDiag = Math.sqrt(w * w + h * h) / 2 + 0.1;
    // Stripe spacing tuned so a 3.5m staircase gets ~12 treads.
    const STEP_M = 0.30;
    const numSteps = Math.max(2, Math.ceil((halfDiag * 2) / STEP_M));
    const clipId = `floorplan-stair-clip-${feature.id}`;

    // SVG clipPath defs: lazily create a single <defs> child on the inner
    // group, then append a per-feature <clipPath> into it. Multiple features
    // share the defs element via their unique clipId.
    const existingDefs = inner.querySelector('defs');
    const defs: SVGDefsElement =
      existingDefs instanceof SVGDefsElement
        ? existingDefs
        : (() => {
            const created = document.createElementNS(svgNS, 'defs') as SVGDefsElement;
            inner.insertBefore(created, inner.firstChild);
            return created;
          })();
    const clip = document.createElementNS(svgNS, 'clipPath');
    clip.setAttribute('id', clipId);
    clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
    const clipPoly = document.createElementNS(svgNS, 'polygon');
    clipPoly.setAttribute('points', points);
    clip.appendChild(clipPoly);
    defs.appendChild(clip);

    const stripes = document.createElementNS(svgNS, 'g');
    stripes.setAttribute('clip-path', `url(#${clipId})`);
    stripes.setAttribute('class', 'floorplan__stair-treads');
    for (let i = -numSteps; i <= numSteps; i++) {
      const t = i * STEP_M;
      // Each stripe passes through (cx + sx*t, cy + sy*t) with direction (ux, uy).
      const x0 = cx + sx * t - ux * halfDiag;
      const y0 = cy + sy * t - uy * halfDiag;
      const x1 = cx + sx * t + ux * halfDiag;
      const y1 = cy + sy * t + uy * halfDiag;
      const line = document.createElementNS(svgNS, 'line');
      line.setAttribute('x1', String(x0));
      line.setAttribute('y1', String(y0));
      line.setAttribute('x2', String(x1));
      line.setAttribute('y2', String(y1));
      stripes.appendChild(line);
    }
    inner.appendChild(stripes);

    // Border on top so the polygon outline is clearly visible over the stripes.
    const border = document.createElementNS(svgNS, 'polygon');
    border.setAttribute('points', points);
    border.setAttribute('class', 'floorplan__stair-border');
    inner.appendChild(border);

    if (feature.label) {
      const text = document.createElementNS(svgNS, 'text');
      text.setAttribute('x', String(cx));
      text.setAttribute('y', String(cy));
      text.setAttribute('transform', `scale(1 -1) translate(0 ${-cy * 2})`);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'central');
      text.setAttribute('class', 'floorplan__stair-label');
      text.textContent = feature.label;
      inner.appendChild(text);
    }
  }
}
